import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { SidebarContext } from 'context/SidebarContext';
import JobtitleServices from 'services/JobtitleServices';
import { notifyError, notifySuccess } from 'utils/toast';

const useJobtitle = (id, jobType, setJobType) => {
	const { isDrawerOpen, closeDrawer, setIsUpdate } = useContext(SidebarContext);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [variants, setVariants] = useState([]); // skills
	const [benefits, setBenefits] = useState([]); // benefits
	const [loading, setLoading] = useState(false);

	const {
		register,
		handleSubmit,
		setValue,
		clearErrors,
		formState: { errors },
		watch,
	} = useForm();

	// -------------------- SKILLS --------------------
	const addVariant = (e) => {
		e.preventDefault();
		const value = e.target.value?.trim();
		if (value) {
			setVariants((prev) => [...prev, value]);
			e.target.value = '';
		}
	};

	const removeVariant = (indexToRemove) => {
		setVariants((prev) => prev.filter((_, index) => index !== indexToRemove));
	};

	// -------------------- BENEFITS --------------------
	const addBenefit = (e) => {
		e.preventDefault();
		const value = e.target.value?.trim();
		if (value) {
			setBenefits((prev) => [...prev, value]);
			e.target.value = '';
		}
	};

	const removeBenefit = (indexToRemove) => {
		setBenefits((prev) => prev.filter((_, index) => index !== indexToRemove));
	};

	// -------------------- SUBMIT --------------------
	const onSubmit = async (data) => {
		setLoading(true);
		try {
			setIsSubmitting(true);

			const JobtitleData = {
				job_roles: data.job_roles,
				description: data.description,
				salary: data.salary,
				skills: variants, // export skills
				benefits: benefits, // export benefits
				job_type: jobType,
				no_of_openings: Number(data.no_of_openings),
				years_of_exp: Number(data.years_of_exp),
				employment_type: data.employment_type,
				workplace_type: data.workplace_type,
				department: data.id_profile,
				location: data.location,
			};

			if (id) {
				const res = await JobtitleServices.updateJobtitle(id, JobtitleData);
				setIsUpdate(true);
				notifySuccess(res.message);
			} else {
				const res = await JobtitleServices.addJobtitle(JobtitleData);
				setIsUpdate(true);
				notifySuccess(res.message);
			}

			closeDrawer();
		} catch (err) {
			notifyError(err?.response?.data?.message || err?.message);
		} finally {
			setIsSubmitting(false);
			setLoading(false);
		}
	};

	// -------------------- FETCH EXISTING DATA --------------------
	const getJobtitleData = async () => {
		try {
			const res = await JobtitleServices.getJobtitleById(id);
			if (res) {
				const data = res.data;

				setValue('job_roles', data.job_roles);
				setValue('description', data.description);
				setValue('salary', data.salary);
				setValue('skills', data.skills);
				setValue('benifits', data.benifits);
				setValue('location', data.location);
				setValue('employment_type', data.employment_type);
				setJobType(data.job_type);
				setValue('years_of_exp', data.years_of_exp);
				setValue('workplace_type', data.workplace_type);
				setValue('no_of_openings', data.no_of_openings);
				setValue('id_profile', data.department);

				// Parse skills
				const parsedSkills = data.skills
					? data.skills
							.replace(/[{}]/g, '')
							.split('","')
							.map((s) => s.replace(/^"|"$/g, '').trim())
					: [];
				setVariants(parsedSkills);

				// Parse benefits
				const parsedBenefits = data.benefits
					? data.benefits
							.replace(/[{}]/g, '')
							.split('","')
							.map((b) => b.replace(/^"|"$/g, '').trim())
					: [];
				setBenefits(parsedBenefits);
			}
		} catch (err) {
			notifyError(err?.response?.data?.message || err?.message);
		}
	};

	// -------------------- RESET ON CLOSE --------------------
	useEffect(() => {
		if (!isDrawerOpen) {
			setValue('job_roles', '');
			setValue('description', '');
			setValue('benifits', '');
			setValue('salary', '');
			setValue('skills', '');
			setVariants([]);
			setBenefits([]);
			setValue('years_of_exp', '');
			setValue('no_of_openings', '');
			setJobType('Fresher');
			setValue('location', '');
			setValue('department', '');
			setValue('employment_type', '');
			setValue('workplace_type', []);
			clearErrors();
			return;
		}

		if (id) {
			getJobtitleData();
		}
		// eslint-disable-next-line
	}, [id, isDrawerOpen]);

	return {
		register,
		handleSubmit,
		onSubmit,
		errors,
		isSubmitting,
		variants,
		benefits, //   exported benefits
		loading,
		setLoading,
		setVariants,
		addVariant,
		removeVariant,
		setBenefits, //   export setter
		addBenefit, //   export add
		removeBenefit, //   export remove
		watch,
		setValue,
	};
};

export default useJobtitle;
